'use client'

import { useRef, useCallback } from 'react'
import Link from 'next/link'
import VideoCardNew from '@/components/VideoCardNew'
import { type MeiliListing } from '@/app/lib/meilisearch'

interface CategoryRowProps {
  title: string
  count?: number
  videos: MeiliListing[]
  categoryName?: string
  seeAllHref?: string
}

const SCROLL_AMOUNT = 900

/**
 * Horizontal scrolling row of video cards with navigation arrows.
 * Used on the home page to display videos grouped by category.
 */
export default function CategoryRow({
  title,
  count,
  videos,
  categoryName,
  seeAllHref,
}: CategoryRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    const distance = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT
    scrollRef.current.scrollBy({ left: distance, behavior: 'smooth' })
  }, [])

  const resolvedHref =
    seeAllHref ?? (categoryName ? `/?category=${encodeURIComponent(categoryName)}` : '/')

  if (videos.length === 0) return null

  return (
    <section
      className="group/row relative"
      style={{ padding: '12px 0 24px' }}
      aria-label={`${title} videos`}
    >
      {/* ── Row Header ── */}
      <div
        className={
          'flex items-baseline gap-3 px-[72px] mb-4 ' +
          'max-lg:px-10 max-md:px-6'
        }
      >
        <h2 className="font-heading text-[22px] tracking-[2px] text-white">
          {title}
        </h2>

        {count !== undefined && count > 0 && (
          <span className="font-condensed text-[13px] font-bold tracking-[1px] text-gold-dim">
            {count} video{count !== 1 ? 's' : ''}
          </span>
        )}

        <Link
          href={resolvedHref}
          className={
            'ml-auto flex items-center gap-1.5 font-condensed text-[12px] font-bold ' +
            'uppercase tracking-[2px] text-gold no-underline ' +
            'opacity-0 transition-opacity duration-200 group-hover/row:opacity-100 ' +
            'focus-visible:opacity-100'
          }
        >
          See All
          <svg
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </Link>
      </div>

      {/* ── Scroll Wrapper ── */}
      <div className="relative">
        {/* Left Arrow */}
        <button
          type="button"
          onClick={() => scroll('left')}
          aria-label="Scroll left"
          className={
            'clip-arrow absolute left-4 top-1/2 z-10 -translate-y-1/2 ' +
            'flex h-11 w-11 cursor-pointer items-center justify-center ' +
            'border border-[rgba(212,160,23,0.5)] text-gold ' +
            'bg-[rgba(5,5,5,0.9)] hover:bg-[rgba(212,160,23,0.15)] ' +
            'opacity-0 transition-opacity duration-200 group-hover/row:opacity-100 ' +
            'focus-visible:opacity-100 ' +
            'max-md:hidden'
          }
        >
          <svg
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          type="button"
          onClick={() => scroll('right')}
          aria-label="Scroll right"
          className={
            'clip-arrow absolute right-4 top-1/2 z-10 -translate-y-1/2 ' +
            'flex h-11 w-11 cursor-pointer items-center justify-center ' +
            'border border-[rgba(212,160,23,0.5)] text-gold ' +
            'bg-[rgba(5,5,5,0.9)] hover:bg-[rgba(212,160,23,0.15)] ' +
            'opacity-0 transition-opacity duration-200 group-hover/row:opacity-100 ' +
            'focus-visible:opacity-100 ' +
            'max-md:hidden'
          }
        >
          <svg
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Scroll Container */}
        <div
          ref={scrollRef}
          className={
            'hide-scrollbar flex gap-3 overflow-x-auto ' +
            'snap-x snap-mandatory ' +
            'px-[72px] py-2 pb-4 scroll-pl-[72px] ' +
            'max-lg:px-10 max-lg:scroll-pl-10 ' +
            'max-md:px-6 max-md:scroll-pl-6'
          }
        >
          {videos.map((video) => (
            <div
              key={video.listing_id}
              className="max-md:w-[220px] max-md:flex-shrink-0"
            >
              <VideoCardNew
                listingId={video.listing_id}
                title={video.videotitle}
                thumbnailUrl={video.thumbnail_url}
                videoId={video.video ?? undefined}
                category={categoryName}
                creatorName={video.user?.name}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
